import { Component, Inject, Input, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { InputComponent } from '@app/core/components/input/input.component';
import { FormButton } from '@app/core/components/form-field-button/form-field-button.component';
import { Style } from '@app/core/services/core-component.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { TerminalButton, TerminalField, TerminalListItem, TerminalNumber, TerminalNumberKey, TerminalScanGet, TerminalService } from '@app/services/terminal.service';

@Component({
    selector: 'tt-generic-terminal-scan-a',
    templateUrl: './generic-terminal-scan-a.component.html',
    styleUrls: ['./generic-terminal-scan-a.component.css'],
    standalone: false,
})
export class GenericTerminalScanAComponent implements OnInit, OnDestroy {
    @Input() public route = 'carriersbuilder';
    public loading = true;
    public error = '';
    public readonly numericKeys: TerminalNumberKey[] = ['number', 'ttNumber', 'ttNumber2', 'ttNumber3'];
    public readonly steps = [-10, -1, 1, 10];
    public readonly selects = ['selectA', 'selectB'] as const;
    public readonly details = ['detailsA', 'detailsB', 'detailsC'] as const;
    public readonly buttons = ['B', 'C', 'D', 'E', 'F'] as const;
    public styles: Record<string, Style> = {};
    public headerStyle: Partial<CSSStyleDeclaration> = {};
    public numberButtons: Record<TerminalNumberKey, FormButton[]> = { number: [], ttNumber: [], ttNumber2: [], ttNumber3: [] };
    public page: TerminalScanGet = {
        scan: { show: true, value: '', focus: true, setFocus: false },
        inputA: { show: false, value: '' }, inputB: { show: false, value: '' },
        number: this.defaultNumber(), ttNumber: this.defaultNumber(), ttNumber2: this.defaultNumber(), ttNumber3: this.defaultNumber(),
        header: { show: false, value: '', fontSize: 0 },
        detailsA: { show: false, value: '' }, detailsB: { show: false, value: '' }, detailsC: { show: false, value: '' },
        background: { class: '', timer: 0 }, list: { show: false, data: [] },
        buttonA: this.defaultButton(), buttonB: this.defaultButton(), buttonC: this.defaultButton(),
        buttonD: this.defaultButton(), buttonE: this.defaultButton(), buttonF: this.defaultButton(),
        selectA: { show: false, enabled: true, value: '', data: [], dataId: '', dataName: '', auto: true },
        selectB: { show: false, enabled: true, value: '', data: [], dataId: '', dataName: '', auto: true },
        scanDelay: 500, sessionData: '', isError: false, errorMessage: '',
    };
    private scanInput?: InputComponent<string>;
    private backgroundTimer?: ReturnType<typeof setTimeout>;
    private readonly changedAt: Record<TerminalNumberKey, number> = { number: 0, ttNumber: 0, ttNumber2: 0, ttNumber3: 0 };
    private destroyed = false;

    @ViewChild('scanInput') public set scanner(input: InputComponent<string> | undefined) {
        this.scanInput = input;
        this.focusScanner();
    }
    constructor(private readonly terminal: TerminalService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator) {
        this.navigator = useNavigatorFor(navigator, this);
    }
    public async ngOnInit(): Promise<void> {
        this.keyboard('hide');
        await this.load(() => this.terminal.init(this.route));
    }
    private defaultNumber(): TerminalNumber { return { show: false, value: 0, options: { decimals: 2 }, placeholder: '', label: '', readonly: false }; }
    private defaultButton(): TerminalButton { return { show: false, enabled: true, text: { value: '', align: 'center' }, class: '', state: '', parms: '' }; }
    public button(type: string): TerminalButton { return this.page[('button' + type) as 'buttonA']; }
    public buttonType(type: string): 'primary' | 'secondary' | 'success' | 'danger' | 'warning' {
        return (['primary', 'success', 'danger', 'warning'] as const).find(kind => this.button(type).class.split(/\s+/).includes('btn-' + kind)) ?? 'secondary';
    }
    public get bindScanFocus(): boolean { return this.page.scan.show === true && this.page.number.show !== true && this.page.scan.focus === true; }
    private focusScanner(): void {
        if (this.page.scan.focus === true || this.page.scan.setFocus === true) this.scanInput?.inputRef?.nativeElement.focus();
    }
    private keyboard(action: 'hide' | 'show'): void {
        const keyboard = (window.navigator as Navigator & { virtualKeyboard?: { hide?(): void; show?(): void } }).virtualKeyboard;
        if (typeof keyboard?.[action] === 'function') keyboard[action]?.();
    }
    private async load(request: () => Promise<TerminalScanGet>): Promise<void> {
        this.loading = true; this.error = '';
        try {
            const page = await request();
            if (!this.destroyed) this.updateLayout(page);
        } catch (error) { if (!this.destroyed) this.error = error instanceof Error ? error.message : String(error); }
        finally { this.loading = false; }
    }
    public updateLayout(page: TerminalScanGet): void {
        this.page = page;
        for (const key of this.numericKeys) {
            const control = page[key]; control.value = parseFloat(String(control.value));
            this.numberButtons[key] = this.steps.filter(step => Math.abs(step) === 10 ? control.show10 === true : control.show1 === true).map(step => ({
                id: String(step), type: 'primary', icon: 'fas fa-' + (step < 0 ? 'minus' : 'plus') + (Math.abs(step) === 10 ? '-circle' : ''),
                show: Math.abs(step) === 10 ? control.show10 : control.show1,
                onClick: () => this.adjustNumber(key, step),
                tooltip: key === 'number' ? String(Math.abs(step)) : '', translateTooltip: false,
            }));
        }
        if (typeof page.header.style === 'string') {
            const style = page.header.style.trim() || '{}';
            this.headerStyle = JSON.parse(style.startsWith('{') ? style : '{' + style + '}');
        } else this.headerStyle = { ...page.header.style };
        for (const key of ['scan', ...this.selects, ...this.numericKeys, ...this.details, 'buttonA', ...this.buttons.map(type => 'button' + type)] as const) {
            const control = this.page[key as keyof TerminalScanGet] as TerminalField;
            const style: Style = typeof control.style === 'string' ? JSON.parse(control.style.trim() || '{}') ?? {} : { ...control.style };
            if (this.details.includes(key as 'detailsA')) style['textarea'] = { fontFamily: '"Arial Monospaced MT Regular", monospace', padding: '5px', fontWeight: '500', ...style['textarea'] };
            this.styles[key] = style;
        }
        if (this.backgroundTimer) clearTimeout(this.backgroundTimer);
        if (page.background.timer > 0) this.backgroundTimer = setTimeout(() => { this.page.background.class = ''; }, page.background.timer);
        this.focusScanner();
    }
    public adjustNumber(type: TerminalNumberKey, step: number): void { this.page[type].value = parseFloat(String(this.page[type].value)) + step; }
    public async onScan(value: string): Promise<void> {
        this.page.scan.value = null;
        // Clear the control even when consecutive scans leave the page value null.
        this.scanInput?.writeValue?.(null);
        if (this.loading) return;
        await this.load(() => this.terminal.scan(this.route, value, this.page.number.value, this.page.ttNumber.value, this.page.sessionData));
    }
    public async onList(item: TerminalListItem): Promise<void> { await this.load(() => this.terminal.list(this.route, item, this.page.sessionData)); }
    public async onSelect(type: 'selectA' | 'selectB', value: string | number): Promise<void> {
        if (!value) return;
        this.page[type].value = value;
        if (this.page[type].auto === true) await this.load(() => this.terminal.select(this.route, type, value, this.page.sessionData));
    }
    public async onNumber(type: TerminalNumberKey, value: number | string): Promise<void> {
        const control = this.page[type];
        if (type !== 'number' && Number(control.value) === Number(value)) return;
        control.value = value;
        if (control.auto !== true || (type !== 'number' && value === '0.00')) return;
        if (Date.now() - this.changedAt[type] < this.page.scanDelay) return;
        this.changedAt[type] = Date.now();
        await this.load(() => this.terminal.number(this.route, type, value, this.page.sessionData));
    }
    public async onButton(type: string, event?: MouseEvent): Promise<void> {
        const button = this.button(type);
        if (this.loading || button.enabled !== true) return;
        if (button.state) {
            this.error = '';
            try {
                const target = { to: button.state, parms: button.parms };
                if (event?.ctrlKey) await this.navigator?.newTab(target); else await this.navigator?.go(target);
            } catch (error) { this.error = error instanceof Error ? error.message : String(error); }
        } else await this.load(() => this.terminal.button(this.route, type, this.page));
    }
    public ngOnDestroy(): void {
        this.destroyed = true;
        if (this.backgroundTimer) clearTimeout(this.backgroundTimer);
        this.keyboard('show');
    }
}
